// src/features/tutor/saveTutorFeedback.js
// The two Firestore writes behind tutor feedback, shared by GroupDetails
// (pairs/groups) and LearnerDetails (one learner) so both save the same shape.
import { db } from '../../firebase/config';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import {
  FEEDBACK_VERSION,
  SESSION_RATINGS_COLLECTION,
} from '../learnerImpact/tutorFeedbackOptions';

// One tutoring_sessions doc per learner for one unit.
// presenceMap: learnerId -> 'present' | 'absent'. Learners not in it are not saved.
// A no-show keeps the old shape (feedback 'absent', status 'not_present') so
// it never counts as coverage anywhere else in the app.
export const saveUnitFeedback = async ({
  tutorId,
  learners,              // [{ id }]
  groupId = null,
  unit,
  presenceMap,
  participationMap = {}, // learnerId -> 'weak' | 'good' | 'excellent' | 'not_observed'
  preparationMap = {},
  durationMinutes,
  durationSeconds,
  sittingId,
}) => {
  const now = Timestamp.now();
  const seconds = durationSeconds != null ? durationSeconds : durationMinutes * 60;
  const startedAt = Timestamp.fromMillis(now.toMillis() - seconds * 1000);

  const writes = learners
    .filter(learner => presenceMap[learner.id])
    .map(learner => {
      const absent = presenceMap[learner.id] === 'absent';
      const record = {
        tutor_id: tutorId,
        learner_id: learner.id,
        unit_id: unit.id,
        unit_title: unit.title,
        lesson_id: unit.lesson_id,
        status: absent ? 'not_present' : 'completed',
        // Overall is asked per session from v2; on units only a no-show is recorded.
        feedback: absent ? 'absent' : null,
        participation: absent ? null : (participationMap[learner.id] || null),
        preparation: absent ? null : (preparationMap[learner.id] || null),
        feedbackVersion: FEEDBACK_VERSION,
        sitting_id: sittingId,
        time_started: startedAt,
        time_ended: now,
        duration_minutes: durationMinutes,
        duration_seconds: seconds,
        created_at: now,
        updated_at: now,
      };
      if (groupId) record.group_id = groupId;
      return addDoc(collection(db, 'tutoring_sessions'), record);
    });

  await Promise.all(writes);
};

// One tutor_session_ratings doc per learner for one session, saved on
// "Closing the session". `log` is the list of units saved in this session:
// [{ unit, presenceMap, durationSeconds, ... }].
export const saveSessionOverall = async ({
  tutorId,
  groupId = null,
  lessonTitle,
  overallMap, // learnerId -> 'great' | 'good' | 'revisit'
  log,
  sittingId,
}) => {
  const now = Timestamp.now();
  const totalSeconds = log.reduce((sum, e) => sum + (e.durationSeconds || 0), 0);
  const firstUnit = log[0]?.unit;

  const writes = Object.entries(overallMap).map(([learnerId, overall]) => {
    // Units this learner was actually present for
    const presentUnits = log.filter(e => e.presenceMap[learnerId] === 'present');
    const record = {
      tutor_id: tutorId,
      learner_id: learnerId,
      sitting_id: sittingId,
      lesson_id: firstUnit?.lesson_id || null,
      lesson_title: lessonTitle || '',
      unit_ids: presentUnits.map(e => e.unit.id),
      unit_titles: presentUnits.map(e => e.unit.title),
      units_count: presentUnits.length,
      total_seconds: totalSeconds,
      total_minutes: Math.round(totalSeconds / 60),
      overall,
      feedbackVersion: FEEDBACK_VERSION,
      time_started: Timestamp.fromMillis(now.toMillis() - totalSeconds * 1000),
      time_ended: now,
      created_at: now,
    };
    if (groupId) record.group_id = groupId;
    return addDoc(collection(db, SESSION_RATINGS_COLLECTION), record);
  });

  await Promise.all(writes);
};